import type { V1ExternalCall } from '@/generated/hooks/grimoire/models';
import type { IResolvedExternalCall } from '@/modules/automation-run/utils/external-call-visibility';

interface IPlannedCall {
  functionName: string;
  call: V1ExternalCall;
}

/**
 * Pairs a run's external calls with the manifest's planned call sites.
 *
 * A result goes to the site at the same source position, so a call in a loop
 * lands under its one site. Without a position it falls back to the first site
 * with the same procedure, and a result matching no site is returned in
 * `unmatched` rather than dropped.
 *
 * @param planned - The step's planned calls, in source order
 * @param results - The run's calls for that step, in execution order
 * @returns Results by planned call, and those matching no planned call
 */
export function matchCallResults(
  planned: IPlannedCall[],
  results: IResolvedExternalCall[] | undefined,
): {
  byCall: Map<V1ExternalCall, IResolvedExternalCall[]>;
  unmatched: IResolvedExternalCall[];
} {
  const byCall = new Map<V1ExternalCall, IResolvedExternalCall[]>();
  const unmatched: IResolvedExternalCall[] = [];

  for (const result of results ?? []) {
    const start = result.call.codeLocation?.start_byte;
    const functionName =
      result.call.bookFunction?.functionName ?? result.call.functionName;
    const site =
      (start !== undefined &&
        planned.find(p => p.call.call_location?.start_byte === start)) ||
      planned.find(p => p.functionName === functionName);

    if (site) {
      byCall.set(site.call, [...(byCall.get(site.call) ?? []), result]);
    } else {
      unmatched.push(result);
    }
  }

  return { byCall, unmatched };
}
